/**
 * GuaranteeSimulationSection - Run a full-year simulation to produce
 * a simulation-backed performance guarantee score.
 *
 * Props:
 *   projectId: string
 *   scenarioId: string
 *   optimization: object (must have .results to enable)
 *   systemSizes: object
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Shield as ShieldIcon } from '@mui/icons-material';
import { vortxColors, formatCurrency } from '@vortx/shared-ui';
import { useGuaranteeSimulation } from '../../hooks/useGuaranteeSimulation';
import GuaranteeScoreCard from './GuaranteeScoreCard';

export default function GuaranteeSimulationSection({
  projectId,
  scenarioId,
  optimization,
  systemSizes,
}) {
  const {
    isRunning,
    progress,
    results,
    error,
    startSimulation,
    reset,
  } = useGuaranteeSimulation(projectId);

  const hasOptimization = Boolean(optimization?.results);
  const progressPct = progress?.total_steps > 0
    ? Math.round((progress.step / progress.total_steps) * 100)
    : 0;
  const dayLabel = progress?.day && progress?.total_days
    ? `Simulating Day ${progress.day}/${progress.total_days}...`
    : 'Starting simulation...';

  // Extract financial data from results (matches backend guarantee_results structure)
  const counterfactual = results?.counterfactual;
  const annualSavingsDollars = counterfactual?.savings_dollars ?? null;
  const baselineCost = counterfactual?.baseline_cost ?? null;
  const optimizedCost = counterfactual?.optimized_cost ?? null;

  // Build scoreData for GuaranteeScoreCard from guarantee_bands + risk_profile
  const bands = results?.guarantee_bands;
  const risk = results?.risk_profile;
  const scoreData = bands ? {
    p50_savings_pct: bands.p50_savings_pct,
    p75_savings_pct: bands.p75_savings_pct,
    p90_savings_pct: bands.p90_savings_pct,
    risk_grade: risk?.risk_grade,
    risk_score: risk?.risk_score,
    band_width_pp: risk?.band_width_pp,
    risk_factors: risk?.risk_factors,
  } : null;

  return (
    <Box>
      {/* Error state */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reset}>
              Dismiss
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Running state */}
      {isRunning && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <ShieldIcon sx={{ color: vortxColors.teal, fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {dayLabel}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progressPct}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: `${vortxColors.teal}22`,
              '& .MuiLinearProgress-bar': { bgcolor: vortxColors.teal },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Full-year (365-day) simulation — this may take a few minutes.
          </Typography>
        </Box>
      )}

      {/* Results state */}
      {!isRunning && results && (
        <Box sx={{ mb: 2 }}>
          {/* Financial summary */}
          {annualSavingsDollars != null && (
            <Box
              sx={{
                bgcolor: `${vortxColors.teal}11`,
                border: `1px solid ${vortxColors.teal}33`,
                borderRadius: 1,
                p: 1.5,
                mb: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Annual Savings: {formatCurrency(annualSavingsDollars)}
              </Typography>
              {baselineCost != null && optimizedCost != null && (
                <Typography variant="caption" color="text.secondary">
                  Baseline: {formatCurrency(baselineCost)} | Optimized: {formatCurrency(optimizedCost)}
                </Typography>
              )}
            </Box>
          )}

          {/* Score card */}
          <GuaranteeScoreCard
            scoreData={scoreData}
            annualSavingsDollars={annualSavingsDollars}
            baselineCost={baselineCost}
            optimizedCost={optimizedCost}
            simulationBacked={true}
          />

          {/* Re-run button */}
          <Button
            variant="text"
            size="small"
            onClick={reset}
            sx={{ mt: 1, color: 'text.secondary' }}
          >
            Reset / Run Again
          </Button>
        </Box>
      )}

      {/* Idle state — show run button */}
      {!isRunning && !results && (
        <Button
          variant="outlined"
          startIcon={<ShieldIcon />}
          disabled={!hasOptimization}
          onClick={() => startSimulation(scenarioId)}
          sx={{
            borderColor: vortxColors.teal,
            color: vortxColors.teal,
            '&:hover': {
              borderColor: vortxColors.teal,
              bgcolor: `${vortxColors.teal}11`,
            },
            '&.Mui-disabled': {
              borderColor: 'action.disabled',
              color: 'action.disabled',
            },
          }}
        >
          Run Performance Guarantee Analysis
        </Button>
      )}

      {!hasOptimization && !isRunning && !results && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Run an optimization first to enable guarantee analysis.
        </Typography>
      )}
    </Box>
  );
}
